/**
 * MODULE: platform/browser/frontend/components/BrowserActionLog.tsx
 * PURPOSE: Renders chat / action log with copy actions for agent results.
 */

import { useCallback, useMemo, useState } from 'react';
import { ClipboardDocumentCheckIcon, ClipboardDocumentIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { dispatchAssistantOpenWithPrefill } from '@design-system/components/AiAssistant/assistantPrefillBridge';
import type { ChatMessage } from '../types/browser.types';

interface ChatMessageItemProps {
  message: ChatMessage;
  onCopy?: (text: string) => void;
  copiedId?: string | null;
}

export function ChatMessageItem({ message, onCopy, copiedId }: ChatMessageItemProps) {
  const base = 'rounded-lg px-3 py-2 text-sm';
  let className = `${base} bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200`;

  if (message.role === 'user') {
    className = `${base} bg-blue-600 text-white`;
  } else if (message.role === 'error') {
    className = `${base} bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200`;
  } else if (message.role === 'assistant') {
    const partial = message.outcome === 'stopped' || message.outcome === 'limit';
    className = partial
      ? `${base} bg-amber-50 text-amber-950 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800`
      : `${base} bg-emerald-50 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100`;
  } else if (message.role === 'action') {
    className = `${base} bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 border border-amber-200 dark:border-amber-800`;
  }

  const canCopy = message.role === 'assistant' && Boolean(message.content.trim());

  return (
    <div className={`${message.role === 'user' ? 'ml-4' : 'mr-4'}`}>
      <div
        className={className}
        data-testid={
          message.role === 'assistant' && message.outcome && message.outcome !== 'success'
            ? `browser-assistant-${message.outcome}`
            : undefined
        }
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">{message.content}</div>
          {canCopy && onCopy ? (
            <button
              type="button"
              onClick={() => onCopy(message.content)}
              className="shrink-0 rounded p-1 text-emerald-800 hover:bg-emerald-100/80 dark:text-emerald-100 dark:hover:bg-emerald-900/40"
              aria-label="Copy result"
              title="Copy result"
            >
              {copiedId === message.id ? (
                <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden />
              ) : (
                <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function BrowserActionLog({
  messages,
  contextPreview,
  canRetryLastCommand = false,
  onRetryLastCommand,
  retryLastCommandLabel = 'Retry last command',
}: {
  messages: ChatMessage[];
  contextPreview?: string;
  canRetryLastCommand?: boolean;
  onRetryLastCommand?: () => void;
  retryLastCommandLabel?: string;
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const lastAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'assistant' && m.content.trim()),
    [messages],
  );

  const handleCopy = useCallback(async (text: string, messageId?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (messageId) {
        setCopiedId(messageId);
        window.setTimeout(() => setCopiedId((prev) => (prev === messageId ? null : prev)), 2000);
      }
    } catch {
      // Clipboard may be unavailable in some embedded contexts.
    }
  }, []);

  const handleDiscussWithAssistant = useCallback(() => {
    if (!lastAssistant) return;
    const ctx = (contextPreview ?? '').trim();
    const blocks = [
      '## Browser automation result',
      lastAssistant.content.trim(),
      ctx ? `## CRM context\n${ctx}` : '',
      '## Follow-up',
      'Based on the browser research above, what are the top 3 next actions I should take in CRM?',
    ].filter(Boolean);
    dispatchAssistantOpenWithPrefill(blocks.join('\n\n'), {
      widgetTitle: 'Browser result',
    });
  }, [contextPreview, lastAssistant]);

  return (
    <div className="flex flex-col gap-3">
      {messages.map((msg) => (
        <ChatMessageItem
          key={msg.id}
          message={msg}
          copiedId={copiedId}
          onCopy={(text) => handleCopy(text, msg.id)}
        />
      ))}
      {canRetryLastCommand && onRetryLastCommand ? (
        <div className="mr-4">
          <button
            type="button"
            onClick={onRetryLastCommand}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
            data-testid="browser-retry-last-command"
          >
            {retryLastCommandLabel}
          </button>
        </div>
      ) : null}
      {lastAssistant ? (
        <div className="mr-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleDiscussWithAssistant()}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-900 hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-950/40 dark:text-primary-100 dark:hover:bg-primary-900/50"
            data-testid="browser-discuss-with-assistant"
          >
            <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5" aria-hidden />
            Discuss with AI
          </button>
          <button
            type="button"
            onClick={() => handleCopy(lastAssistant.content)}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-900/50"
            data-testid="browser-copy-last-result"
          >
            <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden />
            Copy last result
          </button>
        </div>
      ) : null}
    </div>
  );
}
